/**
 * nativeRealtimeLifecycle — Supabase realtime websocket lifecycle management
 * for the Capacitor Android shell (Items 8 + 10, mobile-native cluster).
 *
 * Confirmed failure mode (PostHog issue 019f42b8-3385…, native sessions from
 * https://localhost): the phoenix socket errors at CONNECTION level
 * (conn.onerror → onConnError → triggerChanError) which fans CHANNEL_ERROR
 * out to every joined channel — chat postgres_changes stop delivering and
 * WebRTC signaling channels reject with "Failed to subscribe to signaling
 * channel". REST fetches keep working, so messages appear only after a
 * remount/refetch (background→foreground, navigation).
 *
 * What this module does (native only, initialized once from App.tsx):
 * 1. appStateChange → follows the official Supabase Capacitor guidance:
 *    startAutoRefresh() on resume / stopAutoRefresh() on background, so the
 *    session token can't silently expire while Android suspends WebView
 *    timers (an expired token also kills channel joins).
 * 2. On resume + on network reconnect: if the realtime socket is not
 *    connected, force supabase.realtime.connect() — phoenix then rejoins all
 *    registered channels itself. Emits `behtari:app-resumed` so data layers
 *    (UniversalMessagingContext, hooks) can refetch anything missed while
 *    the socket was down.
 * 3. Telemetry: attaches close/error listeners on the underlying WebSocket
 *    (idempotent) so the actual close code/reason of a failing native socket
 *    reaches PostHog as `realtime-resume` events — the missing ground truth
 *    for why the WebView socket errors.
 */
import { App as CapacitorApp } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { isNativeApp } from '@/lib/nativeApp';
import { supabase } from '@/integrations/supabase/client';
import { captureError } from '@/utils/captureError';
import { isOnlineNow } from '@/utils/networkReady';

let initialized = false;
const instrumentedSockets = new WeakSet<object>();
let reconnectTimestamps: number[] = [];
let lastReconnectCapture = 0;
/** Same 5-min-window throttle already applied to the reconnect-loop signal
 *  above and to `captureChannelFailure` in realtimeChannel.ts — abnormal
 *  socket closes/errors are routine on Android network flaps (background,
 *  network handover) and fire in rapid bursts from a single underlying
 *  incident. Without this, one blip produces N near-duplicate PostHog
 *  issues (observed: 4 issues filed in the same second from one drop). */
const SOCKET_EVENT_CAPTURE_WINDOW_MS = 5 * 60_000;

/** Throttle state lives in sessionStorage, NOT in module variables.
 *
 *  WHY (measured, 2026-08-12). Live events from one native session
 *  (019ff501…, 13:09-13:14 PKT) show socket-close captures landing 20s apart —
 *  far inside the 5-minute window that was supposed to gate them — and EVERY
 *  captured event reports `consecutiveAbnormalCloses: 1` and
 *  `suppressedSinceLastCapture: 0`. Both counters read as freshly initialised
 *  on every capture, which a single long-lived module instance cannot produce:
 *  `onOpen` resets the streak but never touches the throttle timestamp or the
 *  suppressed counter, so at minimum `suppressed` would have to climb.
 *
 *  The consistent reading is that this module is re-evaluated — the Capacitor
 *  WebView reloads its JS context (Android reclaiming a backgrounded WebView is
 *  the usual cause). Module-scoped throttles cannot survive that, so every
 *  reload re-armed the "first capture is always allowed" path. That is the
 *  mechanism behind the flat ~6-13/day baseline that native-shell releases 9,
 *  10 and 11 did not move: the volume is substantially a telemetry artifact,
 *  not a count of distinct incidents.
 *
 *  sessionStorage survives a reload within the same tab/WebView, so the window
 *  now actually holds. It is also the same store the module already trusts for
 *  cross-reload state elsewhere in the app. If sessionStorage is unavailable
 *  the helpers degrade to the old in-memory behaviour rather than throwing. */
const CLOSE_CAPTURE_AT_KEY = 'behtari_rt_close_capture_at';
const CLOSE_SUPPRESSED_KEY = 'behtari_rt_close_suppressed';
const DEAD_CAPTURE_AT_KEY = 'behtari_rt_dead_capture_at';

/** Counts how many times this module has been EVALUATED in one PostHog
 *  session, and how long the current evaluation has been alive.
 *
 *  This is the diagnostic the last investigation lacked. Ruled out against the
 *  installed SDK (@supabase/realtime-js 2.110.1): `socketAdapter` is built once
 *  in the RealtimeClient constructor and `onClose` appends to phoenix's
 *  stateChangeCallbacks, so duplicate handler registration cannot happen — which
 *  leaves "the JS context restarted" as the only explanation for counters that
 *  read as freshly initialised on every capture. If that is right, these fields
 *  make it unarguable: a rising generation inside one session IS the WebView
 *  reloading the whole app, which is a far bigger problem than a dropped socket
 *  and would also explain silently-missed realtime events across the app.
 *
 *  Costs one sessionStorage read+write at module load and nothing thereafter. */
const CONTEXT_GENERATION_KEY = 'behtari_rt_context_generation';
const contextStartedAt = Date.now();
let contextGeneration = 0;

/** In-memory fallback for environments without sessionStorage. */
const memoryStore: Record<string, string> = {};

function readNum(key: string): number {
  try {
    const v = sessionStorage.getItem(key);
    return v === null ? 0 : Number(v) || 0;
  } catch {
    return Number(memoryStore[key]) || 0;
  }
}

function writeNum(key: string, value: number): void {
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    memoryStore[key] = String(value);
  }
}
/** Set by the adapter's error hook and consumed by the very next close.
 *
 *  The socket error and the abnormal close are ONE incident, not two. Measured
 *  over 4 days of live native traffic: every `socket-error` event pairs 1:1
 *  with a `socket-close` on the same URL and device (116/116, 114/114, 83/83,
 *  42/42, …), because a failed WebSocket fires `error` and then immediately
 *  `close(1006)`. Reporting them separately doubled the volume AND split one
 *  condition across two PostHog issues that then had to be triaged twice.
 *
 *  The error event carried nothing that made it worth its own report either:
 *  `detail` was the string `[object Event]` in all 489 captures, because a DOM
 *  Event has no message. So the error side is now folded into the close as a
 *  boolean and never captured on its own. */
let errorPrecededClose = false;
/** True once the phoenix close/error hooks are actually registered. Reported in
 *  the loop capture so a silently-dead diagnostic can never look like silence
 *  from a healthy socket again (see instrumentSocket). */
let instrumentationAttached = false;

/** Consecutive abnormal closes with no successful open in between.
 *
 *  On an affected device the socket does not fail once — it never establishes
 *  at all, and phoenix retries on its capped backoff (1s, 2s, 5s, then 10s
 *  forever). The live gap histogram is exactly that shape: the dominant
 *  interval between abnormal closes is 10-11s, sustained for the whole session
 *  (one device logged 187 closes in 31 minutes).
 *
 *  A 5-minute throttle turns that into a slow permanent drip rather than a
 *  storm, which is better but still wrong: it reports the same dead transport
 *  every 5 minutes for as long as the app is open, and never actually says
 *  "this device cannot open a websocket at all" — which is the thing worth
 *  knowing. So once the failures are clearly not transient we emit ONE
 *  escalated event that names the condition and carries the attempt count, then
 *  stay silent until a successful open proves the transport came back. */
const TRANSPORT_DEAD_AFTER_CONSECUTIVE_CLOSES = 6;

/** The escalation ALSO needs a time window, not just the boolean.
 *
 *  The boolean alone assumed a dead transport stays visibly dead. It does not:
 *  on a flapping device the socket opens briefly (clearing the flag via
 *  `onOpen`) and then racks up another six closes, so "say it once" re-armed
 *  every few seconds. Live proof from session 019ff501… on 2026-08-12 —
 *  transport-dead fired at 13:10:33, 13:13:27 and 13:14:01, three times in
 *  under four minutes, each reporting exactly 6 consecutive closes.
 *
 *  Worse, this branch `return`s BEFORE the socket-close throttle is consulted,
 *  so it was the one capture path with no rate limit at all — the opposite of
 *  what its own comment promised. A 30-minute floor keeps the signal (a device
 *  that genuinely cannot open a websocket still reports, and still says so
 *  loudly) while making one bad session cost one event instead of dozens. */
const TRANSPORT_DEAD_CAPTURE_WINDOW_MS = 30 * 60_000;
let consecutiveAbnormalCloses = 0;
let transportDeclaredDead = false;

/** Real socket state via the PUBLIC RealtimeClient API.
 *
 *  This previously read `supabase.realtime.conn` and mapped `readyState`. That
 *  property does not exist in @supabase/realtime-js 2.110.x — the socket moved
 *  behind `socketAdapter`, and RealtimeClient.js contains zero references to
 *  `this.conn`. So the helper returned the literal string 'no-conn' every time,
 *  which is exactly what the telemetry shows: 100% of 110 `reconnect-loop`
 *  captures in the last 14 days report socketState 'no-conn', regardless of the
 *  socket's actual state. The single most useful field in this diagnostic was a
 *  constant.
 *
 *  isConnected/isConnecting/isDisconnecting are declared on RealtimeClient and
 *  delegate to the adapter, so they survive that refactor. */
function socketState(): string {
  try {
    const rt = supabase.realtime as unknown as {
      isConnected?: () => boolean;
      isConnecting?: () => boolean;
      isDisconnecting?: () => boolean;
    };
    if (rt.isConnected?.()) return 'open';
    if (rt.isConnecting?.()) return 'connecting';
    if (rt.isDisconnecting?.()) return 'closing';
    return 'closed';
  } catch {
    return 'unknown';
  }
}

/** How many channels are currently registered on the client.
 *
 *  Doubles as the "is there anything to recover?" test: with zero joined
 *  channels a down socket is the correct state, not a fault. */
function channelCount(): number {
  try {
    return (supabase.realtime as unknown as { channels?: unknown[] }).channels?.length ?? -1;
  } catch {
    return -1;
  }
}

/** Attach close/error diagnostics to the realtime socket.
 *
 *  This is the fix for a diagnostic that never once fired. It used to read
 *  `supabase.realtime.conn` and attach WebSocket listeners to it. In
 *  realtime-js 2.110.x that property does not exist, so the guard
 *  `if (!conn) return;` bailed on the first line, every time. Live proof: in 60
 *  days there are 198 `reconnect-loop` and 171 `reconnect` captures under the
 *  `realtime-resume` tag and ZERO `socket-close` / `socket-error` captures.
 *  The "ground truth for why the WebView socket errors" this module was written
 *  to collect has never been collected.
 *
 *  It now registers through phoenix's own onClose/onError hooks on the socket
 *  adapter. Those callbacks live in `stateChangeCallbacks`, which phoenix keeps
 *  across reconnects, so registration is ONE-TIME — the old per-WebSocket
 *  re-attach dance (and its WeakSet) is unnecessary.
 *
 *  `socketAdapter` is an internal field, so this is best-effort. Crucially it
 *  records WHETHER it attached (`instrumentationAttached`) and reports that in
 *  the loop capture below, so if a future SDK release moves it again the
 *  telemetry says so instead of silently going dead a second time. */
function instrumentSocket(): void {
  if (instrumentationAttached) return;
  try {
    const adapter = (supabase.realtime as unknown as {
      socketAdapter?: {
        onOpen?: (cb: () => void) => void;
        onClose?: (cb: (ev: CloseEvent) => void) => void;
        onError?: (cb: (err: unknown) => void) => void;
      };
    }).socketAdapter;
    if (!adapter?.onClose || !adapter?.onError) return;
    if (instrumentedSockets.has(adapter)) return;
    instrumentedSockets.add(adapter);

    // A successful open is the only thing that proves the transport works, so
    // it is what clears the failure streak. Without this the counter would only
    // ever climb and a device that recovered would stay marked dead.
    adapter.onOpen?.(() => {
      consecutiveAbnormalCloses = 0;
      transportDeclaredDead = false;
      errorPrecededClose = false;
    });

    adapter.onClose((ev: CloseEvent) => {
      // Normal closes (1000) are noise; abnormal ones are the ground truth
      // we've been missing for the native CHANNEL_ERROR storms.
      const code = ev?.code;
      const precededByError = errorPrecededClose;
      errorPrecededClose = false;
      if (code === 1000) {
        consecutiveAbnormalCloses = 0;
        return;
      }
      // A device with no network is expected to see its socket drop — not a
      // defect worth reporting (mirrors captureChannelFailure's offline guard).
      if (navigator.onLine === false) return;

      consecutiveAbnormalCloses += 1;

      // Past the threshold this is no longer "the socket dropped", it is "this
      // device cannot open a websocket". Say that once, with the evidence, and
      // then stop — rather than repeating the drop forever at throttle cadence.
      if (consecutiveAbnormalCloses >= TRANSPORT_DEAD_AFTER_CONSECUTIVE_CLOSES) {
        if (transportDeclaredDead) return;
        transportDeclaredDead = true;
        // Second gate: a transient open clears the flag above, so without a
        // time floor this path re-fires on every flap (see the constant).
        const deadNow = Date.now();
        if (deadNow - readNum(DEAD_CAPTURE_AT_KEY) < TRANSPORT_DEAD_CAPTURE_WINDOW_MS) return;
        writeNum(DEAD_CAPTURE_AT_KEY, deadNow);
        captureError(
          'realtime-resume',
          new Error(`realtime transport unavailable (${consecutiveAbnormalCloses} consecutive abnormal closes)`),
          {
            stage: 'transport-dead',
            code: code ?? -1,
            reason: ev?.reason || '',
            consecutiveAbnormalCloses,
            channelCount: channelCount(),
            online: navigator.onLine,
            contextGeneration,
            contextAgeMs: Date.now() - contextStartedAt,
          },
        );
        return;
      }

      const now = Date.now();
      const suppressedSocketCloses = readNum(CLOSE_SUPPRESSED_KEY);
      if (now - readNum(CLOSE_CAPTURE_AT_KEY) < SOCKET_EVENT_CAPTURE_WINDOW_MS) {
        writeNum(CLOSE_SUPPRESSED_KEY, suppressedSocketCloses + 1);
        return;
      }
      writeNum(CLOSE_CAPTURE_AT_KEY, now);
      captureError('realtime-resume', new Error(`realtime socket closed (${code ?? 'unknown'})`), {
        stage: 'socket-close',
        code: code ?? -1,
        reason: ev?.reason || '',
        wasClean: ev?.wasClean ?? null,
        // Replaces the separate `socket-error` capture. The error event fires
        // immediately before the close on the same failure and carried no
        // detail of its own, so it is a flag here rather than a second issue.
        precededByError,
        consecutiveAbnormalCloses,
        channelCount: channelCount(),
        online: navigator.onLine,
        suppressedSinceLastCapture: suppressedSocketCloses,
        // >1 within a single session means the WebView restarted the app.
        contextGeneration,
        contextAgeMs: Date.now() - contextStartedAt,
      });
      writeNum(CLOSE_SUPPRESSED_KEY, 0);
    });
    adapter.onError(() => {
      // Deliberately never captured on its own — see errorPrecededClose. The
      // console line stays for on-device debugging.
      errorPrecededClose = true;
      console.warn('[realtime-resume] socket error event (folded into next close)');
    });
    instrumentationAttached = true;
  } catch {
    /* diagnostics only — never break the caller */
  }
}

function reconnectRealtime(trigger: 'resume' | 'network'): void {
  try {
    const rt = supabase.realtime as unknown as {
      isConnected?: () => boolean;
      isConnecting?: () => boolean;
      isDisconnecting?: () => boolean;
      connect: () => void;
      channels?: unknown[];
    };
    const connected = typeof rt.isConnected === 'function' ? rt.isConnected() : false;
    // A socket already mid-handshake is NOT a failure. RealtimeClient.connect()
    // early-returns while isConnecting()/isDisconnecting(), so calling it again
    // here is a no-op — but the old code still counted it toward the loop
    // threshold. Capacitor's Network plugin flaps repeatedly on Android, so
    // three flaps during one slow handshake (routine on Pakistani mobile
    // networks) were enough to report "realtime reconnect looping" when nothing
    // was actually wrong. Only genuine reconnect attempts are counted now.
    const connecting = typeof rt.isConnecting === 'function' ? rt.isConnecting() : false;
    const disconnecting = typeof rt.isDisconnecting === 'function' ? rt.isDisconnecting() : false;
    if (connected || connecting || disconnecting) {
      setTimeout(instrumentSocket, 1000);
      return;
    }

    // Nothing joined means nothing to recover, so do not force a socket open.
    //
    // The loop ESCALATION was already gated on channelCount > 0, but the
    // reconnect itself never was, so an unauthenticated device sitting on the
    // login screen still had a socket forced open on every network flap — and
    // on a device where the transport is failing, phoenix then retried that
    // useless socket every 10s indefinitely. Live traffic shows the cost: 42
    // abnormal closes on `/login` across 6 devices in 4 days, serving no
    // channel and no user-visible feature.
    //
    // Safe because supabase-js opens the socket itself from
    // RealtimeChannel.subscribe() — the next real subscription connects
    // normally, so this only skips connects that had nothing to carry.
    const channels = channelCount();
    if (channels === 0) {
      setTimeout(instrumentSocket, 1000);
      return;
    }

    rt.connect();
    // Forced reconnects are the recovery mechanism WORKING — routine after
    // background/network churn on Android. Verified in PostHog (Jul 2026):
    // 122 captures across 6 sessions, one every 6-9s while the Network
    // plugin flapped, drowning error tracking. Only escalate to PostHog when
    // it loops (≥3 forced reconnects in 60s = socket cannot stay up), and at
    // most once per 5 minutes. Console line stays for device debugging.
    const now = Date.now();
    reconnectTimestamps = reconnectTimestamps.filter((t) => now - t < 60_000);
    reconnectTimestamps.push(now);
    console.info(`[realtime-resume] reconnect forced (${trigger})`, socketState());

    // With zero joined channels there is nothing to recover and nothing the
    // user can perceive — a down socket is the correct state (signed out, or
    // pre-subscribe). 38 of the 110 loop captures in the last 14 days reported
    // channelCount 0, i.e. a third of this issue's volume was noise.
    if (
      channels > 0 &&
      reconnectTimestamps.length >= 3 &&
      now - lastReconnectCapture > 300_000
    ) {
      // Confirmed live (networkReady.ts, 2026-07-29 timeline): this exact
      // signal — 3+ forced reconnects in 60s under trigger "network" — fires
      // when the device has NO usable network route at all, i.e. the
      // Capacitor Network plugin is flapping connected/disconnected rather
      // than the app failing to recover from a real socket fault. `onClose`/
      // `onError` above already skip capturing in that state; this escalation
      // did not, so a dead radio filed a PostHog issue about the recovery
      // mechanism instead of about connectivity. `isOnlineNow()` is the more
      // reliable native check (Capacitor Network.getStatus, not the WebView's
      // stale `navigator.onLine`). Only gates the CAPTURE — the reconnect
      // attempt above always runs regardless of this check.
      void isOnlineNow().then((online) => {
        if (!online) return;
        lastReconnectCapture = now;
        captureError('realtime-resume', new Error(`realtime reconnect looping (${trigger})`), {
          stage: 'reconnect-loop',
          trigger,
          socketState: socketState(),
          channelCount: channels,
          reconnectsInLastMinute: reconnectTimestamps.length,
          // If this is false the close/error listeners never attached, so the
          // absence of socket-close captures means "not instrumented", NOT
          // "the socket never closed abnormally". See instrumentSocket().
          instrumentationAttached,
        });
      });
    }
    setTimeout(instrumentSocket, 1000);
  } catch (e) {
    captureError('realtime-resume', e, { stage: 'reconnect-failed', trigger });
  }
}

/** Initialize once from App.tsx. Inert on web. */
export function initNativeRealtimeLifecycle(): void {
  if (!isNativeApp() || initialized) return;
  initialized = true;

  // Bump once per module evaluation. Reads back >1 only if this whole bundle
  // was re-evaluated inside the same PostHog session — see CONTEXT_GENERATION_KEY.
  contextGeneration = readNum(CONTEXT_GENERATION_KEY) + 1;
  writeNum(CONTEXT_GENERATION_KEY, contextGeneration);

  // Instrument whatever socket exists at startup (or shortly after login).
  setTimeout(instrumentSocket, 3000);

  void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      // Official Supabase Capacitor pattern — resume token auto-refresh
      // before anything tries to (re)join channels with a stale JWT.
      try { supabase.auth.startAutoRefresh(); } catch { /* noop */ }
      reconnectRealtime('resume');
      window.dispatchEvent(new CustomEvent('behtari:app-resumed'));
    } else {
      try { supabase.auth.stopAutoRefresh(); } catch { /* noop */ }
    }
  });

  void Network.addListener('networkStatusChange', ({ connected }) => {
    if (connected) {
      reconnectRealtime('network');
    }
  });
}
